/* eslint-disable no-underscore-dangle */
import { createAction } from '@reduxjs/toolkit';
import type { DevicesState } from '../reducers/device';
import type { AppThunk } from '../store/configureStore';
import { SHOW_API_ERROR } from './api-error';
import { throwOnError } from './utils';

type Paginated<T> = {
  rows: T[];
  count: number;
  next: string | null;
  prev: string | null;
  per_page: number;
};

type ExternalDevice = {
  shop_name: string;
  terminal_id: string;
  is_terminal_invalidated: boolean;
  terminal_keys_id: number;
  shared_key: string;
  shared_key_created_at: string;
  organization_name: string;
};

export const GET_POKEREGIS = createAction<DevicesState['pokeregis']>('GET_POKEREGIS');
export const GET_PAYREGIS = createAction<DevicesState['payregis']>('GET_PAYREGIS');
export const GET_PAYREGI_STOCKS = createAction<DevicesState['payregiStocks']>('GET_PAYREGI_STOCKS');
export const GET_PAYBOXES = createAction<DevicesState['payboxes']>('GET_PAYBOXES');
export const GET_PAYBOX_STOCKS = createAction<DevicesState['payboxStocks']>('GET_PAYBOX_STOCKS');
export const GET_PAYSPOTS = createAction<DevicesState['payspots']>('GET_PAYSPOTS');
export const GET_PAYSPOT_STOCKS = createAction<DevicesState['payspotStocks']>('GET_PAYSPOT_STOCKS');
export const GET_KIOSKS = createAction<DevicesState['kiosks']>('GET_KIOSKS');
export const SHOW_PAYREGI = createAction<DevicesState['payregi']>('SHOW_PAYREGI');
export const ASSIGN_PAYREGI = createAction<DevicesState['payregi']>('ASSIGN_PAYREGI');
export const REASSIGN_PAYREGI = createAction<DevicesState['payregi']>('REASSIGN_PAYREGI'); // Not used ?
export const UNASSIGN_PAYREGI = createAction<DevicesState['payregi']>('UNASSIGN_PAYREGI'); // Not used ?
export const DELETE_PAYREGI = createAction<DevicesState['payregi']>('DELETE_PAYREGI');
export const SHOW_PAYBOX = createAction<DevicesState['paybox']>('SHOW_PAYBOX');
export const ASSIGN_PAYBOX = createAction<DevicesState['paybox']>('ASSIGN_PAYBOX');
export const REASSIGN_PAYBOX = createAction<DevicesState['paybox']>('REASSIGN_PAYBOX'); // Not used ?
export const UNASSIGN_PAYBOX = createAction<DevicesState['paybox']>('UNASSIGN_PAYBOX'); // Not used ?
export const DELETE_PAYBOX = createAction<DevicesState['paybox']>('DELETE_PAYBOX');
export const SHOW_PAYSPOT = createAction<DevicesState['payspot']>('SHOW_PAYSPOT');
export const ASSIGN_PAYSPOT = createAction<DevicesState['payspot']>('ASSIGN_PAYSPOT');
export const REASSIGN_PAYSPOT = createAction<DevicesState['payspot']>('REASSIGN_PAYSPOT'); // Not used ?
export const UNASSIGN_PAYSPOT = createAction<DevicesState['payspot']>('UNASSIGN_PAYSPOT'); // Not used ?
export const DELETE_PAYSPOT = createAction<DevicesState['payspot']>('DELETE_PAYSPOT');
export const CREATE_POKEREGI = createAction<DevicesState['pokeregi']>('CREATE_POKEREGI');
export const UPDATE_POKEREGI = createAction<DevicesState['pokeregi']>('UPDATE_POKEREGI');
export const SHOW_POKEREGI = createAction<DevicesState['pokeregi']>('SHOW_POKEREGI');
export const INVALIDATE_POKEREGI = createAction<DevicesState['pokeregi']>('INVALIDATE_POKEREGI');
export const GET_KIOSK_DETAIL = createAction<DevicesState['kiosk']>('GET_KIOSK_DETAIL');
export const CLEAR_KIOSK_DETAIL = createAction<void>('CLEAR_KIOSK_DETAIL');
export const GET_KIOSK_MAINTENANCES =
  createAction<DevicesState['kioskMaintenances']>('GET_KIOSK_MAINTENANCES');
export const GET_KIOSK_HEALTHS = createAction<DevicesState['kioskHealths']>('GET_KIOSK_HEALTHS');
export const GET_KIOSK_PLAYS = createAction<DevicesState['kioskPlays']>('GET_KIOSK_PLAYS');
export const GET_KIOSK_CASH_COLLECTIONS = createAction<DevicesState['kioskCashCollections']>(
  'GET_KIOSK_CASH_COLLECTIONS'
);
export const GET_KIOSK_PLAY_DETAIL =
  createAction<DevicesState['kioskPlayDetail']>('GET_KIOSK_PLAY_DETAIL');

export const SET_SELECTED_PAYREGI_IDS = createAction<number[]>('SET_SELECTED_PAYREGI_IDS');
export const CLEAR_SELECTED_PAYREGI_IDS = createAction<void>('CLEAR_SELECTED_PAYREGI_IDS');
export const SET_SELECTED_PAYSPOT_IDS = createAction<number[]>('SET_SELECTED_PAYSPOT_IDS');
export const CLEAR_SELECTED_PAYSPOT_IDS = createAction<void>('CLEAR_SELECTED_PAYSPOT_IDS');
export const SET_SELECTED_PAYBOX_IDS = createAction<number[]>('SET_SELECTED_PAYBOX_IDS');
export const CLEAR_SELECTED_PAYBOX_IDS = createAction<void>('CLEAR_SELECTED_PAYBOX_IDS');
export const SET_LAST_DEVICE_PAGE_PATH = createAction<string | null>('SET_LAST_DEVICE_PAGE_PATH');

// --- payregi

export const getPayregis =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payregis?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYREGIS(await response.json()));
  };

export const getPayregisOnShop = (id: string, per_page = 1000, pcbVer?: string[])  => {
    const pcbVerParam = pcbVer ? pcbVer.map(ver => `&pcb_ver[]=${encodeURIComponent(ver)}`).join('') : '';
    return getPayregis(`shop_uuid=${id}&per_page=${per_page}${pcbVerParam}`);
};

export const getPayregiStocks =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payregi-stocks?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYREGI_STOCKS(await response.json()));
  };

export const assignPayregi =
  (payregiId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/payregi-stocks/${payregiId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(ASSIGN_PAYREGI(await response.json()));
  };

export const reAssignPayregi =
  (payregiId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/payregis/${payregiId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(REASSIGN_PAYREGI(await response.json()));
  };

export const unAssignPayregi =
  (payregiId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payregis/${payregiId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(UNASSIGN_PAYREGI(await response.json()));
  };

export const deletePayregi =
  (payregiId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payregi-stocks/${payregiId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(DELETE_PAYREGI(await response.json()));
  };

export const showPayregi =
  (id: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payregis/${id}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(SHOW_PAYREGI(await response.json()));
  };

// --- paybox

export const getPayboxes =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payboxes?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYBOXES(await response.json()));
  };

export const getPayboxesOnShop = (id: string, per_page = 1000) =>
  getPayboxes(`shop_uuid=${id}&per_page=${per_page}`);

export const getPayboxStocks =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/paybox-stocks?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYBOX_STOCKS(await response.json()));
  };

export const assignPaybox =
  (payboxId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/paybox-stocks/${payboxId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(ASSIGN_PAYBOX(await response.json()));
  };

export const reAssignPaybox =
  (payboxId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/payboxes/${payboxId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(REASSIGN_PAYBOX(await response.json()));
  };

export const unAssignPaybox =
  (payboxId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payboxes/${payboxId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(UNASSIGN_PAYBOX(await response.json()));
  };

export const deletePaybox =
  (payboxId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/paybox-stocks/${payboxId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(DELETE_PAYBOX(await response.json()));
  };

export const showPaybox =
  (id: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payboxes/${id}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(SHOW_PAYBOX(await response.json()));
  };

// --- payspot

export const getPayspots =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payspots?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYSPOTS(await response.json()));
  };

export const getPayspotsOnShop = (id: string, per_page = 1000) =>
  getPayspots(`shop_uuid=${id}&per_page=${per_page}`);

export const getPayspotStocks =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payspot-stocks?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_PAYSPOT_STOCKS(await response.json()));
  };

export const assignPayspot =
  (payspotId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/payspot-stocks/${payspotId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(ASSIGN_PAYSPOT(await response.json()));
  };

export const reAssignPayspot =
  (payspotId: string, accountId: string): AppThunk =>
  async (dispatch) => {
    const postData = {
      account_id: accountId,
    };
    const response = await fetch(`/api/device/payspots/${payspotId}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(REASSIGN_PAYSPOT(await response.json()));
  };

export const unAssignPayspot =
  (payspotId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payspots/${payspotId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(UNASSIGN_PAYSPOT(await response.json()));
  };

export const deletePayspot =
  (payspotId: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payspot-stocks/${payspotId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(DELETE_PAYSPOT(await response.json()));
  };

export const showPayspot =
  (id: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/payspots/${id}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(SHOW_PAYSPOT(await response.json()));
  };

export const getPayspotCurrentBillbox = (id: string) =>
  fetch(`/api/device/payspots/${id}/current-billbox`).then(throwOnError);

export const getPayspotBillboxes = (id: string, params: string) =>
  fetch(`/api/device/payspots/${id}/billboxes?${params}`).then(throwOnError);

export const getPayspotStatuses = (id: string, params: string) =>
  fetch(`/api/device/payspots/${id}/status?${params}`).then(throwOnError);

export const getPayspotMaintenances = (id: string, params: string) =>
  fetch(`/api/device/payspots/${id}/maintenances?${params}`).then(throwOnError);

export const getPayspotUnsuccessfulBillsBreakdown = (id: string, params: string) =>
  fetch(`/api/device/payspots/${id}/unsuccessful-bills-breakdown?${params}`).then(throwOnError);

// --- pokeregi

export const getPokeregis =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/pokeregis?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_POKEREGIS(await response.json()));
  };

export const getPokeregisOnShop = (id: string, per_page = 1000) =>
  getPokeregis(`shop_uuid=${id}&per_page=${per_page}`);

export const showPokeregi =
  (serial_number: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/pokeregis/${serial_number}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(SHOW_POKEREGI(await response.json()));
  };

export const invalidatePokeregi =
  (serial_number: string): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/pokeregis/${serial_number}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(INVALIDATE_POKEREGI(await response.json()));
  };

// TODO: infer postData type from docs
export const createPokeregi =
  (postData: Record<string, unknown> = {}): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/pokeregis`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(CREATE_POKEREGI(await response.json()));
  };

export const updatePokeregi =
  (serial_number: string, data = {}): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/pokeregis/${serial_number}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(UPDATE_POKEREGI(await response.json()));
  };

// --- kiosk

export const getKiosks = (query = '') =>
  fetch(`/api/device/kiosks?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const showKiosk =
  (kiosk_id = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosks/${kiosk_id}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_DETAIL(await response.json()));
  };

export const clearKiosk = (): AppThunk => async (dispatch) => {
  dispatch(CLEAR_KIOSK_DETAIL());
};

// TODO: infer postData type from docs
export const createKiosk = (postData: Record<string, unknown> = {}) =>
  fetch('/api/device/kiosks', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError);

export const getKioskMaintenances =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosk-maintenances?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_MAINTENANCES(await response.json()));
  };

export const getKioskHealths =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosk-healths?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_HEALTHS(await response.json()));
  };

export const getKioskPlays =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosk-plays?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_PLAYS(await response.json()));
  };

export const getKioskCashCollections =
  (query = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosk-cash-collections?${query}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_CASH_COLLECTIONS(await response.json()));
  };

// TODO: infer postData type from docs
export const createKioskRemoteInstruction =
  (postData: Record<string, unknown> = {}): AppThunk =>
  async (dispatch) => {
    const response = await fetch('/api/device/kiosk-remote-instruction', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
    }
    // TODO: Anti-pattern, should fix
    return response;
  };

export const getKioskPlayDetail =
  (kiosk_play_id = ''): AppThunk =>
  async (dispatch) => {
    const response = await fetch(`/api/device/kiosk-plays/${kiosk_play_id}`);
    if (!response.ok) {
      dispatch(SHOW_API_ERROR(await response.json()));
      return;
    }
    dispatch(GET_KIOSK_PLAY_DETAIL(await response.json()));
  };

/*
  --- external devices endpoints for;
  /settings/device-keys, /settings/device-keys/create, /settings/device-keys/{id}
*/

export const getExternalDevices = (query = '') =>
  fetch(`/api/device/external-devices?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<Paginated<ExternalDevice>>);

export const createExternalDevices = (postData: {
  private_money_id: string;
  shop_id: string;
  num_devices?: number;
}) =>
  fetch(`/api/device/external-devices`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<Pick<Paginated<ExternalDevice>, 'rows'>>);

export const getExternalDevice = (id: string) =>
  fetch(`/api/device/external-devices/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<ExternalDevice>);

export const updateExternalDevice = (
  id: string,
  postData: {
    is_active: boolean;
  }
) =>
  fetch(`/api/device/external-devices/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<ExternalDevice>);

export const createExternalDeviceSharedKey = (
  id: string,
  postData: {
    regenerate_shared_key: boolean;
  }
) =>
  fetch(`/api/device/external-devices/${id}/regenerate-shared-key`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<ExternalDevice>);
